<template>
  <v-row class="mb-6 thumbnailview-container">
    <!-- DIALOG  - PARTICIPANTS-->
    <v-dialog v-model="showParticipantsDialog" max-width="800" class="rounded-xl">
      <signature-chain-participants
        v-if="showParticipantsDialog" :signature-chain="selectedChain"
        @close="showParticipantsDialog = false"
      >
      </signature-chain-participants>
    </v-dialog>

    <v-menu
      v-model="menuVisible" :position-x="menuX" :position-y="menuY" absolute :close-on-content-click="false"
      @input="handleMenuClose($event)"
    >
      <v-list class="document-options-list">
        <v-list-item class="participants-btn" link @click="showParticipantsOfDoc">
          <v-list-item-icon>
            <v-icon color="primary">mdi-account-multiple</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="body-1">
              {{ $t('signatureChains.participants.title') }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item class="download-btn" link @click="downloadDoc">
          <v-list-item-icon>
            <v-icon color="primary">mdi-tray-arrow-down</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="body-1">{{ $t('dataTable.content.action.download') }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-tooltip bottom :disabled="(canUserDeleteChain(selectedChain))">
          <template #activator="{ on, attrs }">
            <div class="d-inline-block" v-bind="attrs" v-on="on">
              <v-list-item
                link :disabled="!canUserDeleteChain(selectedChain)" class="delete-btn"
                @click="deleteSignatureChain(selectedChain, $event, () => $emit('changed'))"
              >
                <v-list-item-icon>
                  <v-icon color="primary">mdi-delete</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="body-1">{{ $t('dataTable.content.action.delete') }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </div>
          </template>
          <span>{{ $t('dataTable.content.action.cantDeleteExplainer') }}</span>
        </v-tooltip>
        <v-list-item link class="info-btn" @click="showDocInfo">
          <v-list-item-icon>
            <v-icon color="primary">mdi-information-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="body-1">{{ $t('dataTable.content.action.info') }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-menu>

    <!-- INFO MENU - one of the doc actions require this -->
    <v-menu v-model="infoMenuVisible" :position-x="infoMenuX" :position-y="infoMenuY" absolute>
      <v-card v-if="infoMenuVisible">
        <v-card-text>
          <v-container class="black--text body-2">
            <v-row>{{ $t('dataTable.header.documents.id') }} : {{ selectedChain.id }}</v-row>
            <v-row>{{ $t('dataTable.header.documents.createdAt') }} : {{ convertDate(selectedChain.createdAt) }}</v-row>
          </v-container>
        </v-card-text>
      </v-card>
    </v-menu>
    <v-container fluid>
      <slot name="header">
      </slot>
      <v-row
        id="document_table_thumbnail" ref="place" justify="start" align-content="start" align="start"
        :class="dashboardMode ? 'dashboard-mode' : ''"
      >
        <div
          v-for="chain in items" :key="chain.id" fluid class="mx-4 d-inline-block card-container"
          max-width="210px !important" @mouseover="handleChainHover(chain)" @mouseout="handleChainMouseLeave(chain)"
        >
          <v-badge tile overlap offset-x="98%" offset-y="2" :value="!dashboardMode" color="transparent" class="mb-6">
            <template #badge>
              <v-chip id="status-badge" class="status-badge" label :color="getBadgeStyle(chain)">
                <span>{{ getBadgeText(chain) }}</span>
              </v-chip>
            </template>
            <v-checkbox
              v-if="!!multipleSelection && !!chain.origDocument" id="chain_checkbox_thumbnail"
              v-model="selected" color="info" class="multi-select-checkbox" :value="chain"
              @change="handleChainSelection"
            ></v-checkbox>
            <div class="doc-container">
              <a target="_blank" @click="openInNewTab(chain, $event)">
                <document-preview
                  :id="!!chain.batch ? '' : getDocumentIdForPreview(chain)" :asic="!!chain.batch"
                  :encrypted="chain.origDocument?.security === 'ENCRYPTED_WITH_USER_PASSWORD'"
                ></document-preview>
              </a>
            </div>
            <div class="chain-title">
              <long-text :text="chain?.origDocument?.fileName || chain.batch.name" :max-char="16"></long-text>
              <v-btn
                class="document-options-btn" rounded icon bottom
                @click="showMenu(chain, $event['clientX'], $event['clientY'])"
              >
                <v-icon medium>
                  mdi-dots-horizontal-circle-outline
                </v-icon>
              </v-btn>
            </div>

            <div class="chain-actions">
              <transition name="fade">
                <div v-if="!!chain.showSignDecline" class="singing-options">
                  <!-- VIEWER -->
                  <template v-if="isViewOnly()">
                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <div class="d-inline-block" v-bind="attrs" v-on="on">
                          <v-btn
                            disabled outlined
                            class="rounded-lg pa-1 px-2 mr-2 white inprogress-btn text--darken-1"
                          >
                            <v-icon>mdi-file-eye-outline</v-icon>
                            <span>
                              {{ $t('signatureChains.states.viewer') }}
                            </span>
                          </v-btn>
                        </div>
                      </template>
                      <span>{{ $t('signatureChains.states.viewerExplainer') }}</span>
                    </v-tooltip>
                  </template>
                  <template v-if="isInProgress(chain.signatures)">
                    <v-tooltip bottom :disabled="false">
                      <template #activator="{ on, attrs }">
                        <div class="d-inline-block" v-bind="attrs" v-on="on">
                          <v-btn
                            disabled outlined
                            class="rounded-lg pa-1 px-2 mr-2 white inprogress-btn text--darken-1"
                          >
                            <v-icon>mdi-progress-pencil</v-icon>
                            <span>
                              {{ $t('signatureChains.states.inProgress') }}
                            </span>
                          </v-btn>
                        </div>
                      </template>
                      <span>{{ $t('signatureChains.states.inProgressExplainer') }}</span>
                    </v-tooltip>
                  </template>
                  <template v-else>
                    <div class="text-center sign-decline-options">
                      <v-btn v-if="userCanSign && !isApproveMode" text class="pa-1 px-5 mr-1 primary sign-btn" @click="signDoc">
                        <!-- :to="'/document/' + item.origDocument.id + '/customization/signature/' + getUserSignatureId(item)+'/' + getCurrentSignatureId(item)" -->
                        <v-icon>mdi-draw-pen</v-icon>
                        {{ $t('dataTable.content.action.sign') }}
                      </v-btn>
                      <v-btn
                        v-if="userCanSign && isApproveMode"
                        id="approve_button"
                        class="sign-btn pa-1 px-4 mr-2 primary" @click="navToApprove(item)"
                      >
                        <v-icon>mdi-check-circle-outline</v-icon>
                        {{ $t('dataTable.content.action.approve') }}
                      </v-btn>
                      <v-btn
                        v-if="canWithdraw(selectedChain)" outlined color="error"
                        class="pa-1 pr-2 white decline-btn"
                        @click="withdrawSignatureChain(selectedChain.id, $event, () => $emit('changed'))"
                      >
                        <v-icon color="error">mdi-file-undo-outline</v-icon>
                        {{ $t('dataTable.content.action.withdraw') }}
                      </v-btn>
                      <!-- DECLINE -->
                      <v-btn
                        v-if="!!userCanSign && selectedChain.createdBy?.id !== currentUser.id" outlined
                        color="error" class="pa-1 pr-2 mr-2 white decline-btn" @click="showDeclineModal($event)"
                      >
                        <v-icon>mdi-close-circle-outline</v-icon>
                        {{ $t('dataTable.content.action.decline') }}
                      </v-btn>
                    </div>
                  </template>
                </div>
              </transition>
            </div>
          </v-badge>
        </div>

        <!-- 0 STATE -->
        <v-card v-show="items.length === 0" width="100%">
          <v-card-text class="d-flex flex-row justify-center accent--text">
            <v-icon color="accent" class="mr-2">
              mdi-information-outline
            </v-icon>
            {{ noDocuments }}
          </v-card-text>
        </v-card>
      </v-row>
      <decline-reason-modal
        v-if="declineReasonVisible" :visible="declineReasonVisible"
        :signature-id="+selectedUserSignatureId" :current-signature-id="selectedCurrentSignatureId"
        @declined="declineModalClosed" @closed="declineReasonVisible = false"
      >
      </decline-reason-modal>
      <download-mode-menu
        :pos-x="downloadMenuX" :pos-y="downloadMenuY" :visible="downloadMenuVisible"
        :new-tab="downloadModalNewTab"
      >
      </download-mode-menu>
    </v-container>
  </v-row>
</template>

<script>
import DocumentPreview from '@/components/DocumentPreview.vue'
import LongText from '@/components/LongText.vue'
import eventBus, { showConfirmationMenu, showToastError, showToastSuccess } from '@/event-bus.js'
import documents from '@/services/api/documents'
import signatureChains from '@/services/api/signatureChains'
import CommonError from '@/views/CommonError'
import DownloadModeMenu from '@/views/signature/chains/DownloadModeMenu'
import SignatureChainParticipants from '@/views/signature/chains/SignatureChainParticipants.vue'
import SignatureChainStatus from '@/views/signature/chains/SignatureChainStatus.js'
import SignatureChainType from '@/views/signature/chains/SignatureChainType'
import FileSaver from 'file-saver'
import downloadMode from '../views/signature/chains/documentDownloadMode.js'
import DeclineReasonModal from '../views/signature/DeclineReasonModal'
export default {
  components: {
    DocumentPreview,
    LongText,
    SignatureChainParticipants,
    DeclineReasonModal,
    DownloadModeMenu

  },

  props: {
    dashboardMode: {
      type: Boolean,
      default: true
    },
    label: {
      type: String,
      default: 'Documents'
    },
    description: {
      type: String,
      default: 'Your documents'
    },
    noDocuments: {
      type: String,
      default: 'There are no documents'
    },
    status: {
      type: String,
      default: 'ALL'
    },
    items: {
      type: Array,
      default() {
        return []
      }
    },
    multipleSelection: {
      type: Boolean,
      default: false
    }
  },
  emits: ['changed', 'selectionChanged'],

  data() {
    return {

      menuVisible: false,
      infoMenuVisible: false,
      menuX: 0,
      menuY: 0,
      infoMenuX: 0,
      infoMenuY: 0,
      selectedChain: {
        createdAt: '',
        createdBy: {},
        id: 0,
        origDocument: {},
        showSignDecline: false,
        signatures: [],
        signedAt: '',
        signedDocument: {},
        status: '',
        type: '',
        updatedAt: ''

      },
      showParticipantsDialog: false,
      userCanSign: false,
      prevPdfUrl: '',
      // decline modal
      declineReasonVisible: false,
      selectedUserSignatureId: '',
      selectedCurrentSignatureId: '',
      selected: [],

      // download mode menu
      downloadMenuVisible: false,
      downloadMenuX: 0,
      downloadMenuY: 0,
      downloadModalNewTab: false

    }
  },
  computed: {
    currentUser() {
      return this.$store.state.currentUser
    },
    isApproveMode() {
      const isOrdered = this.selectedChain.type === SignatureChainType.ORDERED

      if (!isOrdered) {
        return this.selectedChain.signatures.some(signature => {
          return (
            signature.user?.id === this.currentUser.id &&
            signature.status === SignatureChainStatus.CREATED &&
            !!signature.approver
          )
        })
      } else {
        const firstSignature = this.selectedChain.signatures.find(signature => signature.status === 'CREATED' && signature.user.id === this.currentUser.id)
        return firstSignature.approver
      }
    }

  },
  watch: {
    multipleSelection(newVal) {
      // if (!newVal) {
      this.selected = []
      // }
    },
    items() {
      this.selected = []
    }
  },
  methods: {
    getDocumentIdForPreview(chain) {
      if (chain.signatures) {
        const elem = chain.signatures.findLast(this.isElementHaveSignature)

        return elem ? elem.signedDocument.id : chain.origDocument.id
      } else {
        return chain.origDocument.id
      }
    },
    isElementHaveSignature(item) {
      return item.signedDocument !== undefined && item.signedDocument.id !== undefined
    },
    handleOptionsClick(item, x, y) {
      if (this.menuVisible) {
        this.selectedChain = item
        this.menuX = x
        this.menuY = y
        this.menuVisible = true
      }
    },
    shouldItOverFlow() {
      if (this.dashboardMode) {
        return 'dashboard-mode'
      }
    },
    handleChainHover(chain) {
      // console.log('handleChainHover', chain.id)
      if (!this.menuVisible) {
        this.selectedChain = chain
        // console.log('handleChainHover setting selectedChain', chain.id)
        this.userCanSign = this.canUserSign(chain)

        if (this.userCanSign) {
          this.selectedUserSignatureId = (this.getUserSignatureId()).toString()
          this.selectedCurrentSignatureId = (this.getCurrentSignatureId()).toString()
        }
        chain.showSignDecline = true
      }
    },
    handleChainMouseLeave(chain) {
      this.$set(chain, 'showSignDecline', false)
    },
    showMenu(chain, x, y) {
      // console.log('showMenu chain', chain.id)
      this.selectedChain = chain
      if (this.menuVisible) {
        this.menuVisible = false
        setTimeout(() => {
          this.menuX = x
          this.menuY = y
          this.menuVisible = true
        }, 10)
      } else {
        this.menuX = x
        this.menuY = y
        this.menuVisible = true
      }
    },
    handleMenuClose(ev) {
      if (!ev) {
        // this.selectedChain = {
        //   origDocument: {}
        // }
      }
    },
    showDocInfo(e) {
      this.infoMenuX = e.clientX
      this.infoMenuY = e.clientY
      this.infoMenuVisible = true
    },
    showParticipantsOfDoc() {
      this.showParticipantsDialog = true
    },
    signDoc() {
      const chain = this.selectedChain

      if (!chain.origDocument) {
        this.$store.commit('setCurrentChain', chain)
        this.$router.push({ name: 'AsicSignatureCustomization' })
        return
      }

      const signId = this.getUserSignatureId()
      const currentSignatureId = this.selectedCurrentSignatureId

      // determine the correct document ID to sign
      const latestSigned = chain.signatures.findLast(x => x.signedDocument)
      const docIdToSign = latestSigned ? latestSigned.signedDocument.id : chain.origDocument.id

      const userSignature = chain.signatures.find(sig => sig.id === signId)
      const fieldParams = userSignature?.signatureFieldParameters
      const isInClerkMode = userSignature?.clerkMode

      const query = (fieldParams?.originX != null && fieldParams?.originY != null && fieldParams?.page != null)
        ? {
          originX: fieldParams.originX,
          originY: fieldParams.originY,
          page: fieldParams.page,
          width: fieldParams.width,
          height: fieldParams.height
        }
        : {}

      const path = isInClerkMode
        ? `/document/${docIdToSign}/clerk/${signId}/${currentSignatureId}`
        : `/document/${chain.id}/${docIdToSign}/customization/signature/${signId}/${currentSignatureId}`

      if (Object.keys(query).length) {
        this.$router.push({ path, query })
        return
      }

      this.$store.commit('setCurrentChain', chain)

      this.routerPush(path)
    },
    getUserSignatureId() {
      const userSignature = this.selectedChain.signatures.find(signature => (signature.user?.id === this.currentUser.id && signature.status === SignatureChainStatus.CREATED))
      return userSignature.id || undefined
    },
    navToApprove() {
      this.$store.commit('setCurrentChain', this.selectedChain)
      this.routerPush('/document/' + (this.selectedChain.origDocument.id) + '/approve-customization/' + this.getUserSignatureIdForApprover() + '/' + this.getCurrentSignatureId(this.selectedChain))
    },

    getUserSignatureIdForApprover() {
      const userSignature = this.selectedChain.signatures.find(signature => (signature.user.id === this.currentUser.id && signature.status === SignatureChainStatus.CREATED && signature.approver === true))
      return userSignature.id
    },
    getUserSignatureIdForClerk() {
      const userSignature = this.selectedChain.signatures.find(signature => (signature.user.id === this.currentUser.id && signature.status === SignatureChainStatus.CREATED && signature.clerkMode === true))
      return userSignature.id
    },

    getCurrentSignatureId() {
      let currentSignatureId
      if (this.selectedChain.type === SignatureChainType.ORDERED) {
        currentSignatureId = ''
      } else {
        if (this.selectedChain.currentSignatureId === undefined) {
          currentSignatureId = ''
        } else {
          currentSignatureId = this.selectedChain.currentSignatureId
        }
        // currentSignatureId += this.selectedChain.signatures.findIndex(signature => signature.user.id === this.currentUser.id)
      }

      return currentSignatureId
    },

    async openDownloadModal(event, newTab = false) {
      this.downloadMenuVisible = false // Ensure the menu is closed before showing
      this.downloadModalNewTab = false
      this.$nextTick(() => {
        this.downloadMenuVisible = true
        this.downloadModalNewTab = newTab
      })
      this.downloadMenuX = event.clientX
      this.downloadMenuY = event.clientY

      const answer = await new Promise(resolve => {
        eventBus.$once('downloadModeSelected', data => resolve(data))
      })

      return answer
    },
    async downloadDoc(event) {
      const chain = this.selectedChain

      let mode = downloadMode.ORIGINAL
      if (chain.signingTrailMode === downloadMode.DETACHED) {
        mode = await this.openDownloadModal(event)
        if (mode === 'CANCEL') return
      }

      try {
        if (!chain.origDocument && chain.batch) {
          await this.handleBatchDownload(chain, mode)
          return
        }

        const { docId, fileName } = this.getDocIdAndFileName(chain)
        const documentData = await documents.downloadDocument(docId, mode)
        const downloadFileName = this.getDownloadFileName(fileName, mode)

        await FileSaver.saveAs(documentData, downloadFileName)
      } catch (error) {
        showToastError(this.getErrorMessage(error))
      }
    },
    async handleBatchDownload(chain, mode) {
      let docId = chain.batch.id
      const fileName = chain.batch.name

      if (chain.signatures.every(x => x.status === 'CREATED')) {
        try {
          const batch = await documents.getBatch(chain.batch.id)

          batch.documents.forEach(async doc => {
            const documentData = await documents.downloadDocument(doc.id, mode)
            await FileSaver.saveAs(
              documentData.slice(0, documentData.size, 'application/pdf'),
              doc.fileName
            )
          })
          return
        } catch (error) {
          console.error(error)
        }
      } else {
        // ASIC
        const signatures = chain.signatures.filter(signature => signature.signedAt)
        signatures.sort((a, b) => new Date(b.signedAt) - new Date(a.signedAt))
        docId = signatures[0].signedDocument.id

        try {
          const documentData = await documents.downloadDocument(docId, mode)

          await FileSaver.saveAs(documentData, chain.signedDocument.fileName)
        } catch (error) {
          showToastError(this.getErrorMessage(error))
        }
      }
    },
    getDownloadFileName(fileName, mode) {
      const lastDotIndex = fileName.lastIndexOf('.')

      const hasExtension = lastDotIndex !== -1
      const nameWithoutExt = hasExtension ? fileName.substring(0, lastDotIndex) : fileName
      const extension = hasExtension ? fileName.substring(lastDotIndex) : ''

      if (mode === downloadMode.ORIGINAL && extension.toLowerCase() === '.pdf') {
        return fileName
      }
      if (mode === downloadMode.ORIGINAL && extension.toLowerCase() !== '.pdf') {
        return fileName + '.pdf'
      }

      if (mode === downloadMode.FULL) {
        return nameWithoutExt + '.zip'
      }

      return nameWithoutExt + '-signingtrail' + (hasExtension ? extension : '.pdf')
    },
    getDocIdAndFileName(chain) {
      if (chain.signedDocument) {
        return {
          docId: chain.signedDocument.id,
          fileName: chain.signedDocument.fileName.replace(/\.[^/.]+$/, '')
        }
      }

      const lastSignedDoc = chain.signatures.findLast(x => x.signedDocument)
      return {
        docId: lastSignedDoc?.signedDocument?.id || chain.origDocument.id,
        fileName: chain.origDocument?.fileName || 'Document'
      }
    },

    async downloadBatch(chain, mode) {
      const batch = await documents.getBatch(chain.batch.id)
      for (const doc of batch.documents) {
        const documentData = await documents.downloadDocument(doc.id, mode)
        await FileSaver.saveAs(
          documentData.slice(0, documentData.size, 'application/pdf'),
          doc.fileName
        )
      }
    },
    canUserSign(item) {
      const usersSignatures = item?.signatures?.filter(x => !!x.user && x.user.id === this.currentUser.id)
      if (
        usersSignatures?.length > 0 &&
    usersSignatures.some(signature => signature.status === 'CREATED') &&
    item?.status === SignatureChainStatus.CREATED
      ) {
        const currentUserIndex = item?.signatures?.findIndex(
          signature => usersSignatures.find(x => x.id === signature.id) && signature.status === 'CREATED'
        )
        const previousUserIndex = (currentUserIndex - 1)

        if (currentUserIndex === -1) {
          return false
        }
        if (item.signatures[currentUserIndex].status !== 'CREATED') {
          return false
        }
        if (
          item.type === SignatureChainType.UNORDERED ||
      currentUserIndex === 0 ||
      item.signatures[previousUserIndex].status !== 'CREATED'
        ) {
          return true
        }
      }

      return false
    },

    getBadgeStyle(chain) {
      if (chain.status === SignatureChainStatus.DONE) {
        return 'signed'
      } else if (this.canUserSign(chain)) {
        return 'todo'
      } else if (chain.status === SignatureChainStatus.FAILED) {
        return 'failed error lighten-3 error--text text--darken-2'
      } else {
        return 'pending'
      }
    },
    getBadgeText(chain) {
      if (chain.status === SignatureChainStatus.DONE) {
        return this.$t('signatureChains.states.completed')
      } else if (this.canUserSign(chain)) {
        return this.$t('signatureChains.states.todo')
      } else if (chain.status === SignatureChainStatus.FAILED) {
        return this.$t('signatureChains.states.failedTypes.failed')
      } else {
        return this.$t('signatureChains.states.pending')
      }
    },
    async openInNewTab(chain, event) {
      let mode = downloadMode.ORIGINAL
      if (chain.signingTrailMode === downloadMode.DETACHED) {
        this.downloadModalNewTab = true
        mode = await this.openDownloadModal(event, true)

        if (mode === 'CANCEL') {
          return
        }
        this.downloadModalNewTab = false
      }
      const docId = chain.signedDocument?.id ??
        chain.signatures.findLast(x => x.signedDocument)?.signedDocument.id ??
        chain.origDocument.id

      try {
        if (chain.batch) {
          await this.handleBatchDownload(chain, mode)
        } else {
          const blob = await documents.downloadDocument(docId, mode)
          const url = URL.createObjectURL(blob)
          window.open(url, '_blank', 'noopener,noreferrer')
        }
      } catch (error) {
        showToastError(this.getErrorMessage(error))
      }
    },
    isInProgress(signatures) {
      if (!signatures) return false
      const signatureInProgress = signatures.find(x => !!x.user && x.user.id === this.currentUser.id && x.status === SignatureChainStatus.IN_PROGRESS)
      return !!signatureInProgress
    },
    async showDeclineModal(e) {
      const opts = {
        title: this.$t('dialog.confirmation.title'),
        posX: e.clientX,
        posY: e.clientY
      }
      const conf = await showConfirmationMenu(opts)
      if (!conf) {
        return
      }
      this.selectedCurrentSignatureId = this.getCurrentSignatureId()
      this.selectedUserSignatureId = this.getUserSignatureId()
      this.declineReasonVisible = true
    },

    isViewOnly() {
      const b = this.selectedChain.viewers?.find(x => x.id === this.currentUser.id)
      return !!b
    },
    declineModalClosed() {
      this.declineReasonVisible = false
      this.$emit('changed')
    },
    handleChainSelection(ev) {
      this.$emit('selectionChanged', this.selected)
    }

  }

}
</script>
<style scoped>
.filenameContainer {
  height: 60px;
  max-height: 60px;
  line-height: 16px;
}

.dashboard-mode {
  max-height: 334px;
  overflow-y: hidden;
  overflow-x: hidden;
}

.menu-link {
  font-size: 0.9rem;
}

.v-list-item:hover {
  color: var(--v-primary-base) !important;
}

.singing-options {
  display: inline-block;
  position: absolute;
  bottom: 20%;
  left: 50%;
  margin: auto;
  transform: translateX(-50%) translateY(50%);
}

.decline-btn {
  border-color: var(--v-decline-base) !important;
}

.inprogress-btn {
  border-color: var(--v-grey) !important;
}

.sign-decline-options {
  display: flex;
  flex-direction: row;
  justify-content: space-evenly !important;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.33s;
}

.fade-enter,
.fade-leave-to

/* .fade-leave-active below version 2.1.8 */
  {
  opacity: 0;
}

.card-container {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-template-rows: repeat(5, 1fr);
  grid-column-gap: 0px;
  grid-row-gap: 40px;
}

.doc-container {
  width: 210px;
  grid-area: 1 / 1 / 5 / 6;
}

.chain-title {
  grid-area: 5 / 1 / 6 / 6;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

/* .chain-more-action { grid-area:  5 / 4 / 6 / 6; } */
.chain-actions {
  grid-area: 4 / 2 / 5 / 5;
  /* margin-top: 40px; */
}

.multi-select-checkbox {
  position: absolute;
  bottom: 85%;
  right: 0;
}

.multi-select-checkbox::v-deep .v-input--selection-controls__input {
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 0 0 3px white;
}

</style>
